import { Box, styled, Typography } from '@mui/material';
import { useCallback, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import { IconInbox } from 'assets/img';
import { FileDropzoneFileItem } from './file-dropzone-file-item';
import { FileDropzoneProps } from './file-dropzone.types';

export const FileDropzone = ({
  files,
  onFilesSelected,
  onRemoveFile,
  onCancelUpload,
  onValidityChange,
  title,
  hint,
  labels,
  invalidFileMessage,
  accept,
  maxSize,
  multiple = false,
  disabled,
  hasError,
  'data-testid': dataTestId,
}: FileDropzoneProps) => {
  const [showInvalidFileMessage, setShowInvalidFileMessage] = useState(false);

  const onDrop = useCallback(
    (acceptedFiles: File[]) => {
      if (disabled || acceptedFiles.length === 0) {
        return;
      }
      setShowInvalidFileMessage(false);
      onValidityChange?.(true);
      onFilesSelected(multiple ? acceptedFiles : [acceptedFiles[0]]);
    },
    [disabled, multiple, onFilesSelected, onValidityChange]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    onDropRejected: () => {
      setShowInvalidFileMessage(true);
      onValidityChange?.(false);
    },
    accept,
    maxSize,
    maxFiles: multiple ? undefined : 1,
    multiple,
    disabled,
    noClick: disabled,
    noKeyboard: disabled,
  });

  return (
    <Wrapper>
      <DropArea
        {...getRootProps()}
        isDragActive={isDragActive}
        disabled={!!disabled}
        hasError={!!hasError}
        data-testid={dataTestId}
      >
        <input
          {...getInputProps()}
          data-testid={dataTestId ? `${dataTestId}-input` : 'file-dropzone-input'}
        />
        <IconCircle>
          <IconInbox width={28} height={28} />
        </IconCircle>
        <Typography variant="body1">{title}</Typography>
        <HintRow>
          <span>{hint}</span>
          <BrowseLabel>{labels.browse}</BrowseLabel>
        </HintRow>
      </DropArea>
      {showInvalidFileMessage ? (
        <Typography variant="body2" color="error">
          {invalidFileMessage}
        </Typography>
      ) : null}
      {files.length > 0 ? (
        <FileList data-testid={dataTestId ? `${dataTestId}-file-list` : 'file-dropzone-file-list'}>
          {files.map((item) => (
            <FileDropzoneFileItem
              key={`${item.file.name}-${item.file.size}-${item.file.lastModified}`}
              item={item}
              labels={labels}
              disabled={disabled}
              onRemoveFile={onRemoveFile}
              onCancelUpload={onCancelUpload}
            />
          ))}
        </FileList>
      ) : null}
    </Wrapper>
  );
};

const Wrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2),
  width: '100%',
}));

const DropArea = styled(Box, {
  shouldForwardProp: (prop: string) =>
    prop !== 'isDragActive' && prop !== 'disabled' && prop !== 'hasError',
})<{ isDragActive: boolean; disabled: boolean; hasError: boolean }>(
  ({ theme, isDragActive, disabled, hasError }) => ({
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(2),
    padding: theme.spacing(10, 6),
    borderRadius: theme.spacing(3),
    border: `2px dashed ${
      hasError
        ? theme.palette.lwDanger[100]
        : isDragActive
          ? theme.palette.lwPrimaryAccentLight[100]
          : theme.palette.lwPrimaryAccentDark[40]
    }`,
    backgroundColor: hasError
      ? (theme.palette.lwDanger[10] ?? theme.palette.lwBlack[2])
      : isDragActive
        ? theme.palette.lwPrimaryAccentLight[5]
        : theme.palette.lwBlack[2],
    cursor: disabled ? 'default' : 'pointer',
    opacity: disabled ? 0.5 : 1,
    textAlign: 'center',
    outline: 'none',
    '&:focus-visible': disabled
      ? {}
      : {
          borderColor: hasError
            ? theme.palette.lwDanger[100]
            : theme.palette.lwPrimaryAccentLight[100],
          boxShadow: `0 0 0 2px ${
            hasError ? theme.palette.lwDanger[20] : theme.palette.lwPrimaryAccentLight[10]
          }`,
        },
  })
);

const IconCircle = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: theme.spacing(12),
  height: theme.spacing(12),
  borderRadius: '50%',
  backgroundColor: theme.palette.lwPrimaryAccentLight[10],
  color: theme.palette.lwPrimaryAccentLight[100],
}));

const HintRow = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  color: theme.palette.text.secondary,
  fontFamily: 'Inter',
  fontSize: 12,
  fontWeight: 500,
  lineHeight: '16px',
}));

const BrowseLabel = styled('span')(({ theme }) => ({
  fontFamily: 'Inter',
  fontSize: 12,
  fontWeight: 600,
  lineHeight: '16px',
  color: theme.palette.lwPrimaryAccentLight[100],
}));

const FileList = styled(Box)({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
});
